import { useCallback, useMemo, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { useMutation } from "@apollo/client"

import { useShopify } from "@app/hooks/useShopify"
import { useAnalytics } from "@app/hooks/useAnalytics"
import { useBraze } from "@app/hooks/useBraze"
import { useQueries } from "@app/hooks/useQueries"
import { useConfigContext } from "@app/providers/config"
import { useCartContext } from "@app/providers/cart"
import { useAppContext } from "@app/providers/app"
import { useCore } from "@app/hooks/useCore"
import { Attribute, CartUserError, CartWarning } from "@shopify/hydrogen-react/storefront-api-types"
import { NormalisedVariant } from "@root/types/product"
import { CART_BUYER_IDENTITY_UPDATE } from "@app/graphql/mutations/cart"
import config from "@root/config.js"

export const useCart = () => {
  const {
    helpers: { decodeShopifyId },
  } = useCore()
  const {
    graphql: {
      mutations: { CART_LINE_ADD, CART_LINE_UPDATE, CART_LINE_REMOVE },
    },
  } = useQueries()
  const {
    store: { giftCardType, minimumOrderValue },
  } = useConfigContext()
  const { cart, id: cartId, countryCode, saveCart, refreshCartStorage, loading, setLoading } = useCartContext()
  const { cartNormaliser, formatMoney } = useShopify()
  const { trackCartUpdate } = useAnalytics()
  const { onCartUpdate } = useBraze()
  const { dispatch } = useAppContext()

  const [errors, setErrors] = useState<Array<CartUserError>>([])
  const [warnings, setWarnings] = useState<Array<CartWarning>>([])

  const [cartLinesAdd] = useMutation(CART_LINE_ADD)
  const [cartLinesUpdate] = useMutation(CART_LINE_UPDATE)
  const [cartLinesRemove] = useMutation(CART_LINE_REMOVE)
  const [cartBuyerIdentityUpdate] = useMutation(CART_BUYER_IDENTITY_UPDATE)

  const addToCart = useCallback(
    async (product: any, variant: NormalisedVariant, quantity = 1, attributes: Attribute[] = [], location?: string) => {
      if (!cartId) {
        console.error("Cart ID not found")
        return
      }
      setLoading(true)
      try {
        if (product?.variants?.length > 1 && attributes?.length === 0 && variant?.title) {
          attributes = [{ key: "_label", value: variant?.title }]
        }

        // Remove empty attributes if any, this causes an error in the cart
        attributes = attributes?.filter(({ value }) => !!value)

        const lines = {
          attributes:
            attributes?.map(({ key, value }) => ({
              key,
              value,
            })) || [],
          quantity: quantity,
          merchandiseId: variant.id,
        }

        const {
          data: { cartLinesAdd: data },
        } = await cartLinesAdd({
          variables: {
            cartId,
            lines,
          },
        })

        if (data.userErrors?.length) {
          setErrors(data.userErrors)
        }

        if (data?.warnings?.length) {
          setWarnings(data.warnings)
        }

        if (data?.cart) {
          const normalisedCart = cartNormaliser(data.cart)
          refreshCartStorage(normalisedCart)
          saveCart(data.cart)
          onCartUpdate(normalisedCart)
          dispatch({
            type: "cart",
            payload: true,
          })
          trackCartUpdate(
            "add",
            `gid://shopify/ProductVariant/${decodeShopifyId(variant.id, "ProductVariant")}`,
            quantity,
            normalisedCart?.lines,
            undefined,
            location
          )
        }
      } finally {
        setLoading(false)
      }
    },
    [
      cartLinesAdd,
      cartId,
      cartNormaliser,
      trackCartUpdate,
      decodeShopifyId,
      saveCart,
      onCartUpdate,
      dispatch,
      setLoading,
      refreshCartStorage,
    ]
  )

  const addToCartMultiple = useCallback(
    async (
      items: {
        variantId: string
        quantity: number
        attributes: Attribute[]
      }[] = [],
      isBundleEdit?: boolean
    ) => {
      if (!cartId) {
        console.error("Cart ID not found")
        return
      }
      setLoading(true)
      try {
        const lines =
          items?.map(line => ({
            attributes: (line?.attributes as Attribute[])
              ?.filter(({ value }) => !!value)
              ?.map(({ key, value }) => ({
                key,
                value,
              })),
            quantity: line?.quantity,
            merchandiseId: line?.variantId,
          })) || []

        const {
          data: { cartLinesAdd: data },
        } = await cartLinesAdd({
          variables: {
            cartId,
            lines,
          },
        })

        if (data.userErrors?.length) {
          setErrors(data.userErrors)
        }

        if (data?.warnings?.length) {
          setWarnings(data.warnings)
        }

        if (data?.cart) {
          saveCart(data.cart)
          const normalisedCart = cartNormaliser(data.cart)
          refreshCartStorage(normalisedCart)
          onCartUpdate(normalisedCart)
          dispatch({
            type: "cart",
            payload: true,
          })
          const isBundle = items.some(item => item.attributes.some(attr => attr.key === config.settings.cart.bundleIdAttribute))
          if (isBundle) {
            trackCartUpdate("add", items[0].variantId, items[0].quantity, normalisedCart?.lines, isBundleEdit)
          } else {
            for (const item of items) {
              trackCartUpdate("add", item.variantId, item.quantity, normalisedCart?.lines)
            }
          }
        }
      } finally {
        setLoading(false)
      }
    },
    [cartLinesAdd, cartId, cartNormaliser, saveCart, onCartUpdate, dispatch, trackCartUpdate, setLoading, refreshCartStorage]
  )

  const removeFromCart = useCallback(
    async (id: string, variantId: string) => {
      if (!cartId || !cart?.lines.length) {
        console.error("Cart ID not found")
        return
      }
      setLoading(true)
      try {
        // Check if this item is part of a bundle by looking for _gift_bundle_id attribute
        const targetLine = cart?.lines.find(line => line.id === id)
        const bundleId = targetLine?.attributes?.find(attr => attr.key === config.settings.cart.bundleIdAttribute)?.value

        let lineIds: string[]
        let quantity: number

        if (bundleId) {
          // Remove all items with the same bundle_id
          const bundleLines = cart?.lines.filter(line =>
            line.attributes?.some(attr => attr.key === config.settings.cart.bundleIdAttribute && attr.value === bundleId)
          )
          lineIds = bundleLines.map(line => line.id)
          quantity = bundleLines[0]?.quantity || 1
        } else {
          // Single item removal
          lineIds = cart?.lines.filter(line => line.id === id).map(line => line.id)
          quantity = cart?.lines.filter(line => line.id === id).map(({ quantity }) => quantity)[0] || 1
        }

        const {
          data: { cartLinesRemove: data },
        } = await cartLinesRemove({
          variables: { cartId, lineIds },
        })

        if (data.userErrors?.length) {
          setErrors(data.userErrors)
        }

        if (data?.warnings?.length) {
          setWarnings(data.warnings)
        }

        if (data.cart) {
          const normalisedCart = cartNormaliser(data.cart)
          refreshCartStorage(normalisedCart)
          saveCart(data.cart)
          onCartUpdate(normalisedCart)
          trackCartUpdate("remove", variantId, quantity, cart?.lines || [])
        }
      } finally {
        setLoading(false)
      }
    },
    [cartId, cart, cartLinesRemove, cartNormaliser, refreshCartStorage, saveCart, onCartUpdate, trackCartUpdate, setLoading]
  )

  const removeOosFromCart = useCallback(async () => {
    if (!cartId || !cart) {
      console.error("Cart ID not found")
      return
    }
    setLoading(true)
    try {
      const lineIds = cart?.lines.filter(line => !line?.merchandise?.availableForSale).map(line => line.id)

      const {
        data: { cartLinesRemove: data },
      } = await cartLinesRemove({
        variables: { cartId, lineIds },
      })

      if (data.userErrors?.length) {
        setErrors(data.userErrors)
      }

      if (data?.cart) {
        const normalisedCart = cartNormaliser(data.cart)
        saveCart(data.cart)
        refreshCartStorage(normalisedCart)
        onCartUpdate(normalisedCart)
      }
    } finally {
      setLoading(false)
    }
  }, [cartId, cart, cartLinesRemove, cartNormaliser, saveCart, refreshCartStorage, onCartUpdate, setLoading])

  const updateQuantity = useCallback(
    async (id: string, variantId: string, quantity: number, action = "add") => {
      if (!cartId || !cart) {
        console.error("Cart ID not found")
        return
      }
      setLoading(true)
      try {
        const lines = cart?.lines
          .filter(line => line.id === id)
          .map(line => ({
            id: line.id,
            quantity: quantity,
          }))

        const {
          data: { cartLinesUpdate: data },
        } = await cartLinesUpdate({
          variables: { cartId, lines },
        })

        if (data.userErrors?.length) {
          setErrors(data.userErrors)
        }

        if (data?.warnings?.length) {
          setWarnings(data.warnings)
        }

        if (data?.cart) {
          const normalisedCart = cartNormaliser(data.cart)
          refreshCartStorage(normalisedCart)
          saveCart(data.cart)
          onCartUpdate(normalisedCart)
          trackCartUpdate(action, variantId, quantity, normalisedCart?.lines)
        }
      } finally {
        setLoading(false)
      }
    },
    [saveCart, cart, cartLinesUpdate, cartId, cartNormaliser, trackCartUpdate, onCartUpdate, setLoading, refreshCartStorage]
  )

  const updateBuyerIdentity = useCallback(
    async ({ email, customerAccessToken }: { email: string | null; customerAccessToken: string | null }) => {
      const {
        data: {
          cartBuyerIdentityUpdate: { cart },
        },
      } = await cartBuyerIdentityUpdate({
        variables: { cartId, buyerIdentity: { email, customerAccessToken } },
      })
      saveCart(cart)
    },
    [cartBuyerIdentityUpdate, saveCart, cartId]
  )

  const { shipping, giftWrapping } = useStaticQuery<GatsbyTypes.staticShippingAndPageCartQuery>(graphql`
    query staticShippingAndPageCart {
      shipping: sanitySettingShipping {
        threshold_au
        threshold_nz
        threshold_international
        includeDiscounts
        excludeGiftCards
        messageBefore
        messageAfter
      }
      giftWrapping: sanitySettingGiftWrapping {
        enabled
        product {
          productTitle
          _rawProduct(resolveReferences: { maxDepth: 2 })
        }
        icon
        title
        content: _rawContent(resolveReferences: { maxDepth: 5 })
        image {
          asset {
            url
          }
        }
        color {
          preset
          customCss
          custom {
            hex
          }
        }
        textColor
        bodyColor {
          preset
          customCss
          custom {
            hex
          }
        }
      }
    }
  `)

  const freeShipping = useMemo(() => {
    const includeDiscounts = shipping?.includeDiscounts ?? true
    const excludeGiftCards = shipping?.excludeGiftCards ?? false
    const giftCardTotal =
      cart?.lines?.reduce((total, item) => {
        const productHandle = item.merchandise?.product?.handle
        const isGiftCard = productHandle === config.settings.product.giftcardHandle

        if (isGiftCard) {
          return total + parseFloat(item.cost?.totalAmount?.amount || "0")
        }
        return total
      }, 0) ?? 0

    const baseCartTotal = includeDiscounts ? Number(cart?.cost?.subtotalAmount?.amount) : Number(cart?.cost?.totalAmount?.amount) || 0
    const cartTotalAmount = excludeGiftCards ? baseCartTotal - giftCardTotal : baseCartTotal
    const thresholdCountryCode =
      countryCode.toLowerCase() === "au" ? "threshold_au" : countryCode.toLowerCase() === "nz" ? "threshold_nz" : "threshold_international"
    const threshold = shipping?.[thresholdCountryCode] ?? 100
    const percentage = cartTotalAmount > threshold ? 100 : (cartTotalAmount / threshold) * 100
    const remaining = cartTotalAmount > threshold ? 0 : threshold - cartTotalAmount
    const rawMessage = percentage === 100 ? shipping?.messageAfter : shipping?.messageBefore
    const message = rawMessage?.replace("{remaining}", formatMoney(remaining))
    return { threshold, percentage, message }
  }, [shipping, cart?.lines, cart?.cost?.subtotalAmount?.amount, cart?.cost?.totalAmount?.amount, countryCode, formatMoney])

  const isBelowMinOrder = useMemo(() => {
    const subtotal = Number(cart?.cost?.totalAmount?.amount)
    const isBelowMinOrder = minimumOrderValue > 0 ? subtotal < minimumOrderValue : false
    return isBelowMinOrder
  }, [cart?.cost?.totalAmount?.amount, minimumOrderValue])

  const hasOnlyGiftCards = useMemo(() => {
    const lines = cart?.lines ?? []
    return lines.length > 0 && lines.every(item => item.merchandise.product.productType === giftCardType)
  }, [cart?.lines, giftCardType])

  const unavailableItems = useMemo(() => cart?.lines?.filter(line => !line?.merchandise?.availableForSale) || [], [cart?.lines])

  // TODO: Remove this function after March 2026 - legacy bundle cleanup no longer needed
  // Remove legacy merged bundles (created before Cart Transform was disabled)
  // These have lineComponents which no longer work with the new attribute-based grouping
  const removeLegacyBundles = useCallback(async () => {
    if (!cartId || !cart?.lines?.length) return

    // Find lines with lineComponents (old Cart Transform merged bundles)
    const legacyBundleLines = cart.lines.filter((line: any) => line.lineComponents?.length > 0)

    if (!legacyBundleLines.length) return

    setLoading(true)
    try {
      const lineIds = legacyBundleLines.map(line => line.id)

      const {
        data: { cartLinesRemove: data },
      } = await cartLinesRemove({
        variables: { cartId, lineIds },
      })

      if (data?.cart) {
        const normalisedCart = cartNormaliser(data.cart)
        refreshCartStorage(normalisedCart)
        saveCart(data.cart)
        onCartUpdate(normalisedCart)
      }
    } finally {
      setLoading(false)
    }
  }, [cartId, cart?.lines, cartLinesRemove, cartNormaliser, refreshCartStorage, saveCart, onCartUpdate, setLoading])

  return {
    addToCart,
    addToCartMultiple,
    removeFromCart,
    removeOosFromCart,
    removeLegacyBundles,
    updateQuantity,
    updateBuyerIdentity,
    freeShipping,
    loading,
    errors,
    warnings,
    giftWrapping,
    isBelowMinOrder,
    hasOnlyGiftCards,
    unavailableItems,
  }
}
